import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { SUSPENDED_EVENT } from '../api/client';
import { authKeys, fetchMe } from '../api/queries/auth';
import type { User } from '../api/types';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  /** Call after a successful login/signup so every consumer sees the new session. */
  setUser: (user: User | null) => void;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * The session lives in an httpOnly cookie; the server is the source of truth.
 * We hold the resolved user in the React Query cache under ['auth','me'] rather
 * than duplicating it in component state.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: authKeys.me,
    queryFn: fetchMe,
    select: (d) => d.user,
    staleTime: 60_000,
    retry: false,
  });

  // Suspended mid-session: drop the user immediately so RequireAuth bounces them
  // to /login (where they are told why), and bin every cached page of data the
  // now-dead session had loaded.
  useEffect(() => {
    const onSuspended = () => {
      qc.setQueryData(authKeys.me, { user: null });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
    };
    window.addEventListener(SUSPENDED_EVENT, onSuspended);
    return () => window.removeEventListener(SUSPENDED_EVENT, onSuspended);
  }, [qc]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data ?? null,
      isLoading,
      setUser: (user) => qc.setQueryData(authKeys.me, { user }),
      refresh: () => {
        qc.invalidateQueries({ queryKey: authKeys.me });
      },
    }),
    [data, isLoading, qc],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
